import {Conversation} from "./Conversation";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Spinner} from "flowbite-react";
import {formatTimeStamp} from "./utils";
import {Feedback} from "./Feedback";
import {useSupportCase} from "./CaseHook";
import {useAuth} from "../auth/AuthHook";
import Countdown from "react-countdown";
import {Progress} from "../events/Progress";
import {TitleBox} from "../general/TitleBox";
import {CaseInput2} from "../input/CaseInput2";
import {useCaseActions} from "./useCaseActions";
import {withBase} from "../utils/basePath";
import {ActionButton} from "../components/ActionButton";

interface DiscussionProps {
}


export function Discussion(_props: DiscussionProps) {
    const {t} = useTranslation()
    const {userData} = useAuth()
    const {supportCaseState, caseId, limits} = useSupportCase()
    const [isInCooldown, setIsInCooldown] = useState(false)
    const discussionConversationRef = useRef<HTMLInputElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const closingSectionRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);

    const scrollToCenter = useCallback(() => {
        const lastItemRefCurrent = closingSectionRef.current ?? progressRef.current ?? lastMessageRef.current
        if(lastItemRefCurrent) {
            lastItemRefCurrent.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest"
            })
        }
    }, []);

    const {
        actionEntries,
        handleVersionChange,
        handleAllVersionSelected,
        productVersions,
        showProgress,
        isUploadingAttachment,
        uploadProgressPercent,
    } = useCaseActions({scrollToCenter});

    useEffect(() => {
        scrollToCenter()
    }, [scrollToCenter, supportCaseState?.isLoading, showProgress, supportCaseState?.supportCase?.status]);

    const resetCoolDown = useCallback(() => {
        setIsInCooldown(false)
    }, [setIsInCooldown])

    const supportCase = supportCaseState?.supportCase

    const isReallyInCoolDown = limits.lastUpdated != undefined && Date.now() - limits.lastUpdated < limits.cooldown * 1000
    if (isReallyInCoolDown != isInCooldown) {
        setIsInCooldown(isReallyInCoolDown)
    }
    console.log(`Rendering case id: ${caseId}`)
    if (caseId === undefined) {
        return (
            <div id="discussion-frame" className="grid grid-rows-[1fr_auto_1fr] h-full w-full overflow-y-auto justify-items-center">
                <div className="flex flex-col justify-end items-center w-full">
                    <div id="welcome-message" className="flex flex-col text-2xl text-white text-left items-start px-8 gap-2 pb-6 max-w-[850px] w-full">
                        <div className="flex flex-row gap-4 items-start justify-start">
                            <img src={withBase("favicon.png")} alt={"A"} className="size-6"/>
                            <p>{t("Hi <user>", {user: userData?.name ?? "user"})}</p>
                        </div>
                        <p className="text-5xl">{t("How can I help?")}</p>
                    </div>
                </div>
                <CaseInput2
                    disabled={false}
                    forceEnabled={false}
                    actionConfig={actionEntries}
                    isUploading={isUploadingAttachment}
                    progressPercent={uploadProgressPercent}
                />
                <div/>
            </div>
        )
    }

    console.log(`Messages length: ${supportCase?.messages?.length}`)

    return (
        <div id="discussion-frame" className="flex flex-col h-full w-full overflow-hidden pt-4 items-center">
            <TitleBox lineHidden={true} extraClassNames="items-center max-w-[850px] w-full">
                <div id="case-title-heading" className="flex flex-col text-2xl text-white text-center items-center px-8 py-4 gap-2">
                    <p className="text-3xl">{supportCaseState?.supportCase?.title}</p>
                </div>
            </TitleBox>
            <div id="discussion-conversation-container" className="flex flex-col overflow-y-auto grow w-full pb-[50vh] items-start" ref={discussionConversationRef}>
                <Conversation
                    messages={supportCaseState?.supportCase?.messages ?? []}
                    caseId={caseId}
                    jiraProjectKey={supportCaseState?.supportCase?.issue_tracker?.project_key}
                    isSyncing={supportCaseState?.isSyncing === true}
                    onVersionChange={handleVersionChange}
                    onAllVersionSelected={handleAllVersionSelected}
                    lastMessageRef={lastMessageRef}/>
                {showProgress && (
                    <div className="flex flex-col w-full gap-2 p-6 items-center" ref={progressRef}>
                        <Progress
                            progressMap={supportCaseState?.progress ?? new Map()}
                            excludedActivities={[]}
                        />
                    </div>
                )}
                {supportCaseState?.isSyncing === true && (
                    <div className="flex flex-col w-full gap-2 p-6">
                        <Spinner
                            color="gray"
                            aria-label="Waiting for background sync spinner"
                            size="lg"
                            className="animate-spin"/>
                    </div>
                )}
                {supportCase?.status.toLowerCase() === "closed" && (
                    <div id="closing-section" className="flex flex-col w-full gap-2 p-6" ref={closingSectionRef}>
                        <div className="text-center text-xl text-red-500">
                            {supportCase?.closure_time === undefined || supportCase?.closure_time <= 0 ? "Case is closed" : t("Case is closed at", {closureTime: formatTimeStamp(supportCase?.closure_time)})}
                        </div>
                        <div className="text-center text-xl">
                            {`Self learning state: ${t((supportCase?.learning_extract?.state ?? "none") + " kb status")}`}
                        </div>
                        <Feedback key={caseId}/>
                    </div>
                )}
                {supportCaseState?.supportCase?.status.toLowerCase() == "escalated" && supportCaseState?.supportCase?.issue_tracker != null && (
                    <div className="flex flex-col w-full gap-2 p-6 text-center items-center" ref={closingSectionRef}>
                        <p>This support case has been forwarded to the Engineering Team. Channel: {supportCaseState?.supportCase?.issue_tracker?.issue_tracker_name}</p>
                        {supportCaseState?.supportCase?.issue_tracker?.issue_url != null && (
                            <ActionButton
                                className={`text-left flex-row justify-center disabled:bg-inherit w-fit align-middle`}
                                onClick={() => window.open(supportCaseState?.supportCase?.issue_tracker?.issue_url, "_blank")}>
                                {t("Open in " + supportCaseState?.supportCase?.issue_tracker?.issue_tracker_name)}
                            </ActionButton>
                        )}
                    </div>
                )}
            </div>
            {isInCooldown && (
                <div className="flex flex-col justify-center items-center text-white text-sm py-2">
                    <div>{`You have reached your limits. Wait time until next message: `}</div>
                    <div className="px-2">
                        <Countdown date={(limits.lastUpdated ?? 0) + limits.cooldown * 1000} autoStart={true} onComplete={resetCoolDown}/>
                    </div>
                </div>
            )}
            <CaseInput2
                key={caseId}
                disabled={supportCaseState?.isLoading === true || isInCooldown}
                forceEnabled={Object.keys(productVersions).length > 0}
                actionConfig={actionEntries}
                isUploading={isUploadingAttachment}
                progressPercent={uploadProgressPercent}
            />
        </div>
    )
}
